import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Activity, Upload, MessageCircle, Heart, Tag, Captions } from "lucide-react";
import { Link } from "react-router-dom";

const KIND_ICON = { upload: Upload, comment: MessageCircle, like: Heart, tag: Tag, transcribe: Captions };
const KIND_VERB = {
  upload: "uploaded",
  comment: "commented on",
  like: "liked",
  tag: "tagged",
  transcribe: "transcribed",
};

const timeAgo = (iso) => {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const ActivityFeed = ({ dark = false, limit = 10 }) => {
  const [items, setItems] = useState([]);

  const load = async () => {
    try { const res = await api.get("/activity"); setItems(res.data || []); } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000); // pseudo real-time polling
    return () => clearInterval(t);
  }, []);

  const palette = dark
    ? { surface: "border-white/10 bg-white/5", text: "text-white/85", muted: "text-white/45" }
    : { surface: "border-black/10 bg-white", text: "text-black", muted: "text-black/45" };

  return (
    <div data-testid="activity-feed">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="w-3.5 h-3.5 text-[#E60000]" />
        <div className={`text-[10px] tracking-[0.3em] uppercase font-bold ${dark ? "text-white/70" : "text-black/55"}`}>
          Live activity
        </div>
        <span className="ml-2 w-2 h-2 rounded-full bg-[#E60000] animate-pulse" />
      </div>
      {items.length === 0 ? (
        <div className={`text-sm ${palette.muted}`} data-testid="activity-empty">No activity yet — your squad is quiet.</div>
      ) : (
        <ul className="space-y-1">
          {items.slice(0, limit).map((a) => {
            const Icon = KIND_ICON[a.kind] || Activity;
            const verb = KIND_VERB[a.kind] || a.kind;
            const title = a.payload?.title || a.payload?.tags?.slice(0, 3).join(", ") || a.payload?.text || "—";
            const link = a.payload?.media_id ? `/media/${a.payload.media_id}` : null;
            const inner = (
              <div className={`flex gap-3 px-3 py-2 border ${palette.surface}`} data-testid={`activity-${a.id}`}>
                <Icon className="w-4 h-4 mt-0.5 text-[#E60000]" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm ${palette.text}`}>
                    <span className="font-bold">You</span> {verb} <span className="font-medium truncate">{title}</span>
                  </div>
                  <div className={`text-[10px] font-mono ${palette.muted}`}>{timeAgo(a.created_at)}</div>
                </div>
              </div>
            );
            return (
              <li key={a.id}>
                {link ? <Link to={link}>{inner}</Link> : inner}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
